import { Behavior } from './Behavior';
import { ContentType, LessonMode, QuestionFeedbackState } from '../../Constants';
import UtilsService from '../services/UtilsService';
import questionFeedbackManager from '../managers/QuestionFeedbackManager';
import lessonManager from '../managers/LessonManager';
import responseManager from '../managers/ResponseManager';
import responseService from '../services/ResponseService';
import Auth from '../services/AuthService';
import studentActivityManager from '../managers/StudentActivityManager';
import { fabric } from 'fabric';

export class DrawBehavior extends Behavior {
  checkForValidResponse = (responseAnswer) => {
    if (!responseAnswer || Object.keys(responseAnswer).length === 0) {
      return false;
    }
    return true;
  }

  isAutoScored = (model) => {
    const autoScore = ContentType.getAutoscore(model.type) && !model.survey && !model.unscored;
    return autoScore;
  }

  setResponseData = (data, responseAnswer, model) => {
    responseAnswer = data;
  }

  getInitializedResponse = (lessonElementId) => {
    const responseAnswer = {};
    return responseAnswer;
  }

  getCorrectAnswersText = (model) => {
    return null;
  }

  setCorrectAnswer = (state, model) => {
  }

  resetStudentAnswer = (state) => {
  }

  getQuestionFeedbackText = (model, correctFeedback, incorrectFeedback, correctFeedbackBody, incorrectFeedbackBody, mode, questionIsCorrect, currentAttemptCount, { lessonElementState }) => {
    let textBody = '';
    if (mode === LessonMode.PREVIEW || mode === LessonMode.ITEM_PREVIEW || mode === LessonMode.STUDENT_PREVIEW || mode === LessonMode.PUBLISHER_PREVIEW) {
      textBody += UtilsService.previewWarning();
    } else {
      if (typeof currentAttemptCount !== 'undefined' && currentAttemptCount !== -1 && currentAttemptCount > 1) {
        textBody += UtilsService.saveWarning();
      } else {
        textBody += UtilsService.saveCommit();
      }
    }

    let textHeader = '';
    let attemptsText = '';
    
    // Add attempts remaining text for practice mode when answer is incorrect or partially correct
    if (questionFeedbackManager.isPractice && 
        (questionIsCorrect === QuestionFeedbackState.INCORRECT || questionIsCorrect === QuestionFeedbackState.PARTIAL_CORRECT) &&
        model.lessonElementId) {
      attemptsText = questionFeedbackManager.getAttemptsRemainingText(model.lessonElementId);
    }

    if (questionIsCorrect === QuestionFeedbackState.CORRECT) {
      textHeader = correctFeedback;
      if (correctFeedbackBody) {
        textBody = `${correctFeedbackBody} ${textBody}`;
      }
    } else if (questionIsCorrect === QuestionFeedbackState.PARTIAL_CORRECT) {
      textHeader = `${UtilsService.partiallyCorrect()}`;
      let custom = '';
      if (incorrectFeedbackBody) {
        custom = `${incorrectFeedbackBody }<br/>`;
        textBody = `${UtilsService.partiallyCorrectSelectPrompt()}.<br/>${ custom }${textBody}`;
      } else {
        textBody = `${UtilsService.partiallyCorrectSelectPrompt2()}. ${textBody}`;
      }
    } else {
      textHeader = incorrectFeedback;
      if (incorrectFeedbackBody) {
        textBody = `${incorrectFeedbackBody} ${textBody}`;
      }
    }

    // Add attempts text after the header if it exists
    if (attemptsText) {
      textHeader += attemptsText;
    }

    let finalText = '<div class=\'question-feedback-header\'>';
    finalText += textHeader;
    finalText += '</div>';
    finalText += '<div class=\'question-feedback-body\'>';
    finalText += textBody;
    finalText += '</div>';

    return finalText;
  }

  isAnswerCorrect = (answerId, lessonElementState, model) => {
    if (lessonElementState.correctAnswerShowing) {
      return true;
    }

    return lessonElementState.isScored && (lessonElementState.submittedItemScore === lessonElementState.maxScore);
  }

  isQuestionCorrect = (lessonElementState) => {
    if (!lessonElementState) {
      return QuestionFeedbackState.INCORRECT;
    }

    if (lessonElementState.submittedItemScore >= lessonElementState.maxScore) {
      return QuestionFeedbackState.CORRECT;
    }

    if ((lessonElementState.submittedItemScore > 0) && (lessonElementState.submittedItemScore < lessonElementState.maxScore)) {
      return QuestionFeedbackState.PARTIAL_CORRECT;
    }

    return QuestionFeedbackState.INCORRECT;
  }

  // Response dependency methods
  hasResponseDependency = (model, lessonElementId) => {
    const responseDependency = lessonManager.lessonOptions?.responseDependency;
    const responseDependencyValue = responseDependency && responseDependency[lessonElementId];
    
    if (!responseDependencyValue) {
      return false;
    }
    
    // Handle new format where value is an object
    if (typeof responseDependencyValue === 'object') {
      if (responseDependencyValue.source === 'inLesson') {
        return responseDependencyValue.questionId;
      } else if (responseDependencyValue.source === 'differentLesson') {
        // For differentLesson, check if lessonId and questionForeverId are not null/undefined/empty
        return responseDependencyValue.lessonId && 
               responseDependencyValue.questionForeverId &&
               responseDependencyValue.lessonId !== '' &&
               responseDependencyValue.questionForeverId !== '';
      }
    }
    
    // Handle old format where value is a string (questionId)
    return responseDependencyValue !== null && responseDependencyValue !== '';
  }

  getPreviousResponse = (model, lessonElementId) => {
    if (!this.hasResponseDependency(model, lessonElementId)) {
      return null;
    }

    const responseDependency = lessonManager.lessonOptions?.responseDependency;
    const responseDependencyValue = responseDependency[lessonElementId];
    
    let previousQuestionId;
    
    // Handle new format where value is an object
    if (typeof responseDependencyValue === 'object') {
      if (responseDependencyValue.source === 'inLesson') {
        previousQuestionId = responseDependencyValue.questionId;
      } else if (responseDependencyValue.source === 'differentLesson') {
        // For differentLesson source, call API to get prior responses
        this.fetchPriorResponsesFromDifferentLesson(responseDependencyValue, lessonElementId);
        return null; // Return null for now, will be handled by the API call
      } else {
        return null;
      }
    } else {
      // Handle old format where value is a string (questionId)
      previousQuestionId = responseDependencyValue;
    }
    
    const previousQuestionState = responseManager.getLessonElementState(previousQuestionId);
    
    if (!previousQuestionState || !previousQuestionState.currentResponseAnswer) {
      return null;
    }

    return previousQuestionState.currentResponseAnswer;
  }

  fetchPriorResponsesFromDifferentLesson = async (responseDependencyValue, lessonElementId) => {
    try {
      const { lessonId, questionForeverId } = responseDependencyValue;
      const activityId = studentActivityManager.studentActivityId;
      
      if (!activityId) {
        console.warn('No activity ID available for fetching prior responses');
        return;
      }

      const response = await Auth.fetch(`${Auth.ecms}/api/getPriorResponses?lessonForeverId=${lessonId}&questionForeverId=${questionForeverId}&activityId=${activityId}`, {
        method: 'GET',
      });

      console.log('Prior responses from different lesson:', response);
      
      if (response && response.responses && Array.isArray(response.responses)) {
        const responses = response.responses;
        
        // Filter out empty responses
        const nonEmptyResponses = responses.filter(resp => {
          try {
            const content = JSON.parse(resp.content);
            return content.drawing && Object.keys(content.drawing).length > 0;
          } catch (e) {
            return false;
          }
        });
        
        if (nonEmptyResponses.length === 0) {
          // Show empty response warning
          responseService.alertEmptyPreviousResponse();
          return;
        }
        
        if (nonEmptyResponses.length === 1) {
          // Single response - populate directly
          const singleResponse = nonEmptyResponses[0];
          try {
            const content = JSON.parse(singleResponse.content);
            const lessonElementState = responseManager.getLessonElementState(lessonElementId);
            if (lessonElementState) {
              lessonElementState.setCurrentResponse(UtilsService.safeMobxClone(content));
            }
          } catch (e) {
            console.error('Error parsing single response content:', e);
            responseService.alertEmptyPreviousResponse();
          }
        } else {
          // Multiple responses - show selection dialog
          this.showResponseSelectionDialog(nonEmptyResponses, lessonElementId);
        }
      } else {
        responseService.alertEmptyPreviousResponse();
      }
    } catch (error) {
      console.error('Error fetching prior responses from different lesson:', error);
      responseService.alertEmptyPreviousResponse();
    }
  }

  populateWithPreviousResponse = (lessonElementId, model) => {
    const previousResponse = this.getPreviousResponse(model, lessonElementId);

    if (previousResponse && Object.keys(previousResponse).length > 0) {
      const lessonElementState = responseManager.getLessonElementState(lessonElementId);
      if (lessonElementState) {
        lessonElementState.setCurrentResponse(UtilsService.safeMobxClone(previousResponse));
      }
      
    } else {
      // Show alert if previous response is empty
      responseService.alertEmptyPreviousResponse();
    }
  }

  hasCurrentContent = (lessonElementId) => {
    const lessonElementState = responseManager.getLessonElementState(lessonElementId);
    if (!lessonElementState || !lessonElementState.currentResponseAnswer) {
      return false;
    }
    
    const currentResponse = lessonElementState.currentResponseAnswer;
    // For Draw questions, check if there's any meaningful content
    return currentResponse && Object.keys(currentResponse).length > 0;
  }

  showResponseSelectionDialog = async (responses, lessonElementId) => {
    try {
      const { showResponseSelectionDialog } = await import('../components/dialogs/ResponseSelectionDialog');
      
      const responseData = responses.map((resp, index) => {
        try {
          const content = JSON.parse(resp.content);
          return {
            id: resp.id,
            index: index + 1,
            content: content,
            creationDate: resp.creationTimestamp,
            modificationDate: resp.modificationTimestamp,
            activityName: resp.activityName
          };
        } catch (e) {
          return null;
        }
      }).filter(Boolean);

      const selectedResponse = await showResponseSelectionDialog({
        responses: responseData,
        type: 'draw'
      });

      if (selectedResponse) {
        const lessonElementState = responseManager.getLessonElementState(lessonElementId);
        if (lessonElementState) {
          lessonElementState.setCurrentResponse(UtilsService.safeMobxClone(selectedResponse.content));
        }
      }
    } catch (error) {
      console.error('Error showing response selection dialog:', error);
    }
  }
}

export default new DrawBehavior();
